import React, { useCallback, useEffect, useState } from 'react';
import { Eye, EyeOff, ExternalLink, Loader2, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/components/ui/use-toast';
import { supabase } from '@/integrations/supabase/client';

type Props = {
  userId: string | null;
};

function maskToken(token: string): string {
  if (!token) return '';
  if (token.length <= 12) return '••••';
  return `${token.slice(0, 4)}${'•'.repeat(8)}${token.slice(-4)}`;
}

/**
 * Lets a signed-in user store their HuggingFace access token so we can push
 * trained LoRA adapters to their personal HF account. The token lives in
 * profiles.huggingface_token (RLS-protected — only the owner reads/writes).
 * We never expose it through our control plane database; it travels only
 * with the user's own training job request.
 */
export default function ConnectHuggingFaceCard({ userId }: Props) {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [stored, setStored] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const [showDraft, setShowDraft] = useState(false);

  const load = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('huggingface_token')
        .eq('id', userId)
        .maybeSingle();
      if (error) throw error;
      setStored(data?.huggingface_token || null);
    } catch (e: any) {
      toast({
        title: 'Could not load HuggingFace status',
        description: String(e?.message || e),
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }, [userId, toast]);

  useEffect(() => {
    void load();
  }, [load]);

  const save = async () => {
    if (!userId) return;
    const token = draft.trim();
    if (!token) return;
    if (!token.startsWith('hf_')) {
      toast({
        title: 'That does not look like a HuggingFace token',
        description: "HuggingFace tokens start with 'hf_'. Generate one at huggingface.co/settings/tokens.",
        variant: 'destructive',
      });
      return;
    }
    setSaving(true);
    try {
      const { error } = await supabase
        .from('profiles')
        .update({ huggingface_token: token })
        .eq('id', userId);
      if (error) throw error;
      setStored(token);
      setDraft('');
      setShowDraft(false);
      toast({
        title: 'HuggingFace connected',
        description: 'Future training runs will publish to your HuggingFace account.',
      });
    } catch (e: any) {
      toast({
        title: 'Could not save token',
        description: String(e?.message || e),
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const disconnect = async () => {
    if (!userId) return;
    setSaving(true);
    try {
      const { error } = await supabase
        .from('profiles')
        .update({ huggingface_token: null })
        .eq('id', userId);
      if (error) throw error;
      setStored(null);
      toast({
        title: 'HuggingFace disconnected',
        description: 'New training runs will no longer publish to your account.',
      });
    } catch (e: any) {
      toast({
        title: 'Could not disconnect',
        description: String(e?.message || e),
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="border-canada-red/30 bg-gradient-to-br from-canada-red/10 via-background to-background shadow-[0_0_0_1px_rgba(234,56,76,0.08)]">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">

          <svg viewBox="0 0 95 88" className="h-5 w-5" aria-hidden="true">
            {/* HuggingFace yellow face glyph — keeps the brand recognition without
                pulling in their official asset bundle. */}
            <path
              fill="#FFD21E"
              d="M47.7 7.4c-22.6 0-41 18.3-41 41 0 22.6 18.4 41 41 41s41-18.4 41-41-18.4-41-41-41z"
            />
            <circle cx="31" cy="44" r="6" fill="#3A3B45" />
            <circle cx="64" cy="44" r="6" fill="#3A3B45" />
            <path
              fill="#3A3B45"
              d="M30 60c0 9.4 7.6 17 17 17s17-7.6 17-17H30z"
            />
          </svg>
          HuggingFace
          {!loading && stored && (
            <span className="inline-flex items-center gap-1 text-xs font-normal text-emerald-500">
              <CheckCircle2 className="h-3 w-3" /> Connected
            </span>
          )}
        </CardTitle>
        <CardDescription>
          Add your HuggingFace token and we'll publish your trained models
          to your account so you can deploy, share, or use them anywhere.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {loading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin text-canada-red" /> Loading…
          </div>
        ) : stored ? (
          <div className="flex items-center justify-between gap-2 rounded-md border border-canada-red/20 bg-canada-red/5 p-3">

            <div className="min-w-0">
              <p className="text-sm font-medium">Token saved</p>
              <p className="text-xs text-muted-foreground font-mono break-all">
                {maskToken(stored)}
              </p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => void disconnect()}
              disabled={saving}
            >
              Disconnect
            </Button>
          </div>
        ) : (
          <>
            <div className="space-y-2">
              <Label htmlFor="hf-token" className="text-xs uppercase tracking-wide text-muted-foreground">
                Access token
              </Label>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Input
                    id="hf-token"
                    type={showDraft ? 'text' : 'password'}
                    placeholder="hf_..."
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    autoComplete="off"
                    className="border-canada-red/20 bg-canada-red/5 pr-9 font-mono"
                  />

                  <button
                    type="button"
                    onClick={() => setShowDraft((v) => !v)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    aria-label={showDraft ? 'Hide token' : 'Show token'}
                  >
                    {showDraft ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                <Button onClick={() => void save()} disabled={saving || !draft.trim()}>
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Connect'}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                Needs <span className="font-medium">write</span> access. Create one at{' '}
                <a
                  href="https://huggingface.co/settings/tokens"
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-0.5 text-canada-red hover:underline"
                >
                  huggingface.co/settings/tokens
                  <ExternalLink className="h-3 w-3" />
                </a>
                . We store it in your profile and only use it to push your
                trained models — it never leaves your account context.
              </p>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
